// src/utils/paymentRecovery.js
// 🎯 [결제 복구] onIncompletePaymentFound 콜백에서 호출되는 중앙화된 복구 유틸리티
//
// 근본 원인:
//   Pi SDK는 블록체인에 출금됐으나 developer_completed: false인 결제가 남아있으면
//   해당 유저의 모든 새 결제를 영구 차단함.
//   onIncompletePaymentFound는 이미 txid를 포함한 PaymentDTO를 전달하므로,
//   이 시점에 즉시 서버 /complete를 호출해야 차단이 해제됨.

import { recoverPendingPayment, cancelPayment, completePayment } from '../services/api';
import { debugLog, errorLog } from './environment';

const RECOVERY_STORAGE_KEY = 'pi-incomplete-payment';
const FAILED_RECOVERY_KEY = 'pi-failed-recovery';

/**
 * Pi SDK의 onIncompletePaymentFound 콜백에서 전달받은 payment 객체를 처리합니다.
 * txid가 있으면 직접 completePayment를, 없으면 서버의 recoverPendingPayment를 호출합니다.
 * 모두 실패하면 localStorage에 기록해 다음 시도를 위해 ID를 보존합니다.
 *
 * @param {object} payment - Pi SDK의 PaymentDTO 객체
 * @param {string} environment - 'sandbox' | 'production'
 */
export const handleIncompletePayment = async (payment, environment) => {
  if (!payment?.identifier) {
    debugLog('[PaymentRecovery] payment 객체 또는 identifier가 없음 - 무시');
    return;
  }

  const paymentId = payment.identifier;
  // Pi SDK의 PaymentDTO에는 이미 transaction.txid가 포함되어 있음 (블록체인 출금 완료된 경우)
  const txid = payment.transaction?.txid || payment.transaction?.id;

  debugLog('[PaymentRecovery] 미완료 결제 처리 시작:', { paymentId, txid, environment });

  // 복구 시도 중 식별자를 sessionStorage에 보존 (중간에 실패해도 재시도 가능하도록)
  try {
    sessionStorage.setItem(RECOVERY_STORAGE_KEY, JSON.stringify({ identifier: paymentId, txid, savedAt: Date.now() }));
  } catch (e) {
    errorLog('[PaymentRecovery] sessionStorage 저장 실패', e);
  }

  try {
    if (txid) {
      // txid가 있으면 Pi API 재조회 없이 즉시 /complete 호출 (가장 빠른 복구 경로)
      debugLog('[PaymentRecovery] txid 있음 → 직접 completePayment 호출:', { paymentId, txid });
      await completePayment(paymentId, txid, environment);
      debugLog('[PaymentRecovery] ✅ 직접 completePayment 성공 - 결제 차단 해제됨:', paymentId);
    } else {
      // txid가 없으면 서버가 Pi API에서 직접 조회 후 complete 시도
      debugLog('[PaymentRecovery] txid 없음 → recoverPendingPayment 호출:', paymentId);
      const result = await recoverPendingPayment(paymentId, environment);

      if (result?.recovered) {
        debugLog('[PaymentRecovery] ✅ recoverPendingPayment 성공 - 결제 차단 해제됨:', paymentId);
      } else {
        // 블록체인 미확정(txid 없음) 상태이므로 안전하게 취소
        debugLog('[PaymentRecovery] 복구 불가(txid 미확정) → cancelPayment 시도:', paymentId);
        await cancelPayment(paymentId, environment);
        debugLog('[PaymentRecovery] ✅ cancelPayment 성공 - 결제 차단 해제됨:', paymentId);
      }
    }

    // 성공 시 임시 저장 데이터 정리
    sessionStorage.removeItem(RECOVERY_STORAGE_KEY);
    localStorage.removeItem(FAILED_RECOVERY_KEY);

  } catch (error) {
    errorLog('[PaymentRecovery] ❌ 복구 실패:', { paymentId, error: error?.message });

    // 실패해도 sessionStorage는 유지 (AppContext.checkIncompletePayment가 재시도할 수 있도록)
    // localStorage에도 백업 저장하여 세션이 종료되어도 재시도 가능하게 함
    try {
      localStorage.setItem(FAILED_RECOVERY_KEY, JSON.stringify({
        paymentId,
        txid: txid || null,
        failedAt: Date.now(),
        environment,
        error: error?.message || 'unknown'
      }));
    } catch (storageError) {
      errorLog('[PaymentRecovery] localStorage 백업 저장 실패', storageError);
    }
  }
};
